import {
  Attachment,
  AttachmentFile,
  Conversation,
  Emoji,
  EmojiAction,
  Id,
  Member,
  Message,
  MessageAction,
  Resource,
  Timestamp,
  User,
} from '@nextforce/api'
import { makeStyles, useTheme } from '@rneui/themed'
import { t } from 'app/i18l'
import { ChatConfig } from 'chat/config/types'
import { groupMessages, groupPinnedMessages } from 'chat/shared/util'
import { partition } from 'lodash'
import React, { useCallback, useMemo, useRef, useState } from 'react'
import { Swipeable } from 'react-native-gesture-handler'
import { IMessage as ChatMessage } from 'react-native-gifted-chat'
import { Menu } from 'react-native-popup-menu'
import { SafeAreaView } from 'react-native-safe-area-context'
import { TextStyle } from 'react-native/Libraries/StyleSheet/StyleSheetTypes'
import EmojiPicker, { EmojiType } from 'rn-emoji-keyboard'
import { confirmationAlert } from 'shared/alerts'
import ActionSheetMenu from 'shared/components/chat/action_sheet_menu'
import { BubbleMessagesView } from './message_bubble'
import { NewsfeedMessagesView } from './newsfeed_message'

type Props = {
  user: User | undefined
  conversation: Conversation | undefined
  messages: readonly Message[] | undefined
  pinnedMessages: readonly Message[] | undefined
  online: boolean
  prefillMessageText: string | undefined
  chatConfig: ChatConfig | undefined
  messageId?: string
  isNewsfeedLayout: boolean
  readByCounts?: {
    [messageId: string]: number
  }
  messageBody: string
  selectedAttachments: AttachmentFile[]
  isUploading: boolean
  members: readonly Member[] | null | undefined
  setChatMessageBody: (text: string) => void
  onSendMessage(): Promise<void>
  onShowFile(attachment: Attachment, isPinnedMessage?: boolean): void
  onSendDirectMessage(recipientId: string): void
  onRemoveMessage(id: string): void
  onShowReaders(id: string): void
  onShowReactors(messageId: string, emoji: Emoji): void
  onCreateMentions(messageId: string): void
  onHideDescription(): void
  onCopyMessage(message?: Message): void
  onSendReaction(id: string, emoji: Emoji): void
  onPinMessage(id: string): void
  onUnpinMessage(id: string): void
  onMarkMessage(id: string): void
  onUnmarkMessage(id: string): void
  onLinkPress(url: string): void
  onCreateComposeMessage(): void
  onClearScrolling(): void
  onShowMultiAttachments: (messageId: string, attachmentIndex: number) => void
  pasteAttachments(chatGroupId: string, files: AttachmentFile[]): void
  onPressAcknowledgeResource(resource: Resource): void
  replyMessage: Message | undefined
  setReplyMessage: (message: Message | undefined) => void
  previousLastViewedTimestamp: Timestamp | undefined
  isLoadingEarlier: boolean
  hasMoreMessages: boolean | undefined
  onLoadEarlier: () => void
}

export default function MessagesView({
  user,
  conversation,
  messages,
  pinnedMessages,
  online,
  prefillMessageText,
  chatConfig,
  messageId,
  isNewsfeedLayout,
  readByCounts,
  messageBody,
  selectedAttachments,
  isUploading,
  setChatMessageBody,
  onCreateComposeMessage,
  onSendMessage,
  onShowFile,
  onRemoveMessage,
  onShowReaders,
  onHideDescription,
  onSendDirectMessage,
  onCopyMessage,
  onSendReaction,
  onShowReactors,
  onCreateMentions,
  onPinMessage,
  onUnpinMessage,
  onMarkMessage,
  onUnmarkMessage,
  onLinkPress,
  onClearScrolling,
  onShowMultiAttachments,
  pasteAttachments,
  onPressAcknowledgeResource,
  replyMessage,
  setReplyMessage,
  members,
  previousLastViewedTimestamp,
  isLoadingEarlier,
  hasMoreMessages,
  onLoadEarlier,
}: Props) {
  const [currentMessage, setCurrentMessage] = useState<Message>()
  const [currentActions, setCurrentActions] = useState<MessageAction[]>()
  const [shouldFocusInput, setShouldFocusInput] = useState(false)

  const swipeableRef = useRef<Swipeable | null>(null)

  const [isEmojiKeyBoardOpen, setIsEmojiKeyBoardOpen] = useState<boolean>(false)
  const [showPinned, setShowPinned] = useState<boolean>(false)
  const styles = useStyles()
  const { theme } = useTheme()
  const readOnly = conversation?.postsAllowed === 'none'

  const { groupedMessages, messageIdToGroupIndex } = useMemo(
    () =>
      groupMessages(
        isNewsfeedLayout
          ? messages?.filter(m => !m.isDeleted)
          : messages?.filter(m => {
              if (m.isDeleted) {
                return !!m.user.id
              }
              return true
            })
      ),
    [isNewsfeedLayout, messages]
  )

  const {
    groupedMessages: groupedPinnedMessages,
    messageIdToGroupIndex: pinnedMessageIdToGroupIndex,
  } = useMemo(() => groupPinnedMessages(pinnedMessages), [pinnedMessages])

  const chatMessages: ChatMessage[] = useMemo(
    () =>
      groupedMessages
        .map(group => convertGroupToChatMessage(user, group))
        .reverse(),
    [groupedMessages, user]
  )

  const pinnedChatMessages: ChatMessage[] = useMemo(
    () =>
      groupedPinnedMessages
        .map(group => convertGroupToChatMessage(user, group))
        .reverse(),
    [groupedPinnedMessages, user]
  )

  const onSetReplyMessageAction = useCallback(
    (message: Message) => {
      setReplyMessage(message)
      setShouldFocusInput(true)
    },
    [setReplyMessage, setShouldFocusInput]
  )

  const getMessageGroup = useCallback(
    (id?: Id) => {
      if (id && messageIdToGroupIndex.has(id)) {
        const groupIndex = messageIdToGroupIndex.get(id)
        if (groupIndex !== undefined) {
          return groupedMessages[groupIndex]
        }
      }
      return undefined
    },
    [groupedMessages, messageIdToGroupIndex]
  )

  const getPinnedMessageGroup = useCallback(
    (id?: Id) => {
      if (id && pinnedMessageIdToGroupIndex.has(id)) {
        const groupIndex = pinnedMessageIdToGroupIndex.get(id)
        if (groupIndex !== undefined) {
          return groupedPinnedMessages[groupIndex]
        }
      }
      return undefined
    },
    [groupedPinnedMessages, pinnedMessageIdToGroupIndex]
  )

  const menuRef = useRef<Menu>(null)

  const getMessageGroupIndexByMessageId = useCallback(
    (id: Id) => {
      const groupMessageId = getMessageGroup(id)?.[0]?.id
      if (groupMessageId) {
        let chatMessageIndex = chatMessages?.findIndex(
          m => m._id === groupMessageId
        )

        if (chatMessageIndex === -1 || chatMessageIndex === undefined) {
          chatMessageIndex = 0
        }
        return chatMessageIndex
      }
      return 0
    },
    [chatMessages, getMessageGroup]
  )

  const messageIndex = useMemo(() => {
    if (messageId) {
      return getMessageGroupIndexByMessageId(messageId)
    }
    return undefined
  }, [getMessageGroupIndexByMessageId, messageId])

  const isPinned = useCallback(
    (id: Id) => {
      return pinnedMessages?.findIndex(m => m.id === id) !== -1
    },
    [pinnedMessages]
  )

  const getAllowedActions = useCallback(
    (
      message: Message,
      isPinnedMessage?: boolean
    ): MessageAction[] | undefined => {
      if (conversation) {
        if (isPinnedMessage) {
          // For pinned messages, only show unpin action
          return [{ name: 'unpinMessage' }]
        }

        const isSender = message.user.id === user?.id
        const isTextMessage = !!message.body
        const actions: MessageAction[] = conversation.messageActions.filter(
          action => {
            if (!action) {
              return false
            }
            switch (action.name) {
              case 'sendDirectMessage':
                return !isSender
              case 'deleteOwn':
                return isSender
              case 'deleteOthers':
                return !isSender
              case 'showReadersOwn':
                return isSender
              case 'showReadersOthers':
                return !isSender
              case 'copyMessage':
                return isTextMessage
              case 'reaction':
                return chatConfig?.allowReactions
              case 'pinMessage':
                return chatConfig?.allowPinMessages
              case 'mark':
                return chatConfig?.allowMarkMessages
              case 'reply':
                return !readOnly
              default:
                return false
            }
          }
        )

        if (isPinned(message.id)) {
          actions[actions.findIndex(m => m.name === 'pinMessage')] = {
            name: 'unpinMessage',
          }
        }
        if (messages?.find(m => m.id === message.id)?.isMarked) {
          actions[actions.findIndex(m => m.name === 'mark')] = {
            name: 'unmark',
          }
        }

        const [otherActions, deleteActions] = partition(
          actions,
          action =>
            action.name !== 'deleteOwn' && action.name !== 'deleteOthers'
        )

        return [...otherActions, ...deleteActions]
      }
    },
    [
      chatConfig?.allowMarkMessages,
      chatConfig?.allowPinMessages,
      chatConfig?.allowReactions,
      conversation,
      isPinned,
      messages,
      readOnly,
      user?.id,
    ]
  )

  const getMessage = useCallback(
    (id?: Id) => messages?.find(m => m.id === id),
    [messages]
  )

  const getPinnedMessage = useCallback(
    (id?: Id) => pinnedMessages?.find(m => m.id === id),
    [pinnedMessages]
  )

  const onSendMessageAction = useCallback(
    (message: Message) => {
      onSendDirectMessage(message.user.id)
    },
    [onSendDirectMessage]
  )

  const onDeleteMessageAction = useCallback(
    async (message: Message) => {
      const confirmed = await confirmationAlert(
        t('deleteMessage'),
        t('deleteMessageConfirmation')
      )
      if (confirmed) {
        onRemoveMessage(message.id)
      }
    },
    [onRemoveMessage]
  )

  const onMarkMessageAction = useCallback(
    (message: Message) => {
      onMarkMessage(message.id)
    },
    [onMarkMessage]
  )
  const onUnmarkMessageAction = useCallback(
    (message: Message) => {
      onUnmarkMessage(message.id)
    },
    [onUnmarkMessage]
  )

  const onMentionMessageAction = useCallback(
    (message: Message) => {
      onCreateMentions(message.id)
    },
    [onCreateMentions]
  )

  const onShowReadersAction = useCallback(
    (message: Message) => {
      onShowReaders(message.id)
    },
    [onShowReaders]
  )

  const onReactMessageAction = useCallback(
    (msgId: string, reaction: Emoji) => {
      onSendReaction(msgId, reaction)
    },
    [onSendReaction]
  )

  const onPinMessageAction = useCallback(
    (message: Message) => {
      onPinMessage(message.id)
    },
    [onPinMessage]
  )
  const onUnpinMessageAction = useCallback(
    (message: Message) => {
      onUnpinMessage(message.id)
    },
    [onUnpinMessage]
  )

  const onCopyMessageAction = useCallback(
    (message: Message) => {
      onCopyMessage(getMessage(message.id))
    },
    [getMessage, onCopyMessage]
  )

  const onMessageAction = useCallback(
    (message: Message, action: MessageAction) => {
      switch (action.name) {
        case 'sendDirectMessage':
          onSendMessageAction(message)
          break
        case 'deleteOwn':
        case 'deleteOthers':
          onDeleteMessageAction(message).catch(() => null)
          break
        case 'showReadersOwn':
        case 'showReadersOthers':
          onShowReadersAction(message)
          break
        case 'copyMessage':
          onCopyMessageAction(message)
          break
        case 'reaction':
          onReactMessageAction(message.id, action.emoji)
          break
        case 'pinMessage':
          onPinMessageAction(message)
          break
        case 'unpinMessage':
          onUnpinMessageAction(message)
          break
        case 'mark':
          onMarkMessageAction(message)
          break
        case 'unmark':
          onUnmarkMessageAction(message)
          break
        case 'reply':
          onSetReplyMessageAction(message)
          break
      }
    },
    [
      onSendMessageAction,
      onDeleteMessageAction,
      onShowReadersAction,
      onCopyMessageAction,
      onReactMessageAction,
      onPinMessageAction,
      onUnpinMessageAction,
      onMarkMessageAction,
      onUnmarkMessageAction,
      onSetReplyMessageAction,
    ]
  )

  const onAction = useCallback(
    (action: MessageAction) => {
      if (!currentMessage) {
        return
      }
      onMessageAction(currentMessage, action)
    },
    [currentMessage, onMessageAction]
  )

  const onEmojiSelected = useCallback(
    ({ emoji }: EmojiType) => {
      if (!currentMessage) return
      const action: EmojiAction = {
        name: 'reaction',
        emoji: emoji,
      }
      onMessageAction(currentMessage, action)
    },
    [currentMessage, onMessageAction]
  )

  const onLongPress = useCallback(
    (context: any, message?: Message) => {
      if (!message) {
        return
      }
      const actions = getAllowedActions(message)
      setCurrentActions(actions)
      setCurrentMessage(message)
      menuRef.current?.open().catch(() => null)
    },
    [getAllowedActions]
  )

  const parsePatterns: ((linkStyle: TextStyle) => any) | undefined =
    useCallback(
      (linkStyle: TextStyle) => [
        {
          type: 'url',
          style: linkStyle,
          onPress: async (text: string) => {
            await onLinkPress(text)
          },
        },
      ],
      [onLinkPress]
    )

  const getBackGroundColor = useCallback(() => {
    if (!readOnly) return { backgroundColor: '' }

    const backgroundColor = showPinned
      ? theme.mode === 'dark'
        ? theme.colors?.grey6
        : theme.colors.teal
      : theme.mode === 'dark'
      ? theme.colors?.grey7
      : theme.colors?.grey5

    return { backgroundColor }
  }, [
    readOnly,
    showPinned,
    theme.colors?.grey5,
    theme.colors?.grey6,
    theme.colors?.grey7,
    theme.colors.teal,
    theme.mode,
  ])

  const { firstUnreadMessageId, unreadMessageCount, firstUnreadMessageIndex } =
    useMemo(() => {
      if (!messages || !previousLastViewedTimestamp || !user?.id) {
        return { firstUnreadMessageId: undefined, unreadMessageCount: 0 }
      }

      const lastViewedAt = previousLastViewedTimestamp

      const lastViewedTimestamp = new Date(lastViewedAt).getTime()
      let firstUnreadIndex = -1

      for (let i = 0; i < messages.length; i++) {
        const message = messages[i]
        if (
          message.user.id !== user.id &&
          new Date(message.createdAt).getTime() > lastViewedTimestamp
        ) {
          firstUnreadIndex = i
          break
        }
      }

      if (firstUnreadIndex !== -1) {
        const count = messages.length - firstUnreadIndex

        const messageIdToReturn = messages[firstUnreadIndex].id

        const firstUnreadMessageGroupIndex = getMessageGroupIndexByMessageId(
          messages[firstUnreadIndex].id
        )

        return {
          firstUnreadMessageId: messageIdToReturn,
          unreadMessageCount: count,
          firstUnreadMessageIndex: firstUnreadMessageGroupIndex,
        }
      }

      return {
        firstUnreadMessageId: undefined,
        unreadMessageCount: 0,
        firstUnreadMessageIndex: undefined,
      }
    }, [
      getMessageGroupIndexByMessageId,
      messages,
      previousLastViewedTimestamp,
      user?.id,
    ])

  const [firstUnreadMessageIndexValue, setFirstUnreadMessageIndexValue] =
    useState<number | undefined>(firstUnreadMessageIndex)
  const [firstUnreadMessageIdValue, setFirstUnreadMessageIdValue] = useState<
    Id | undefined
  >(firstUnreadMessageId)

  const onClearScrollToFirstUnreadMessage = useCallback(() => {
    setFirstUnreadMessageIndexValue(undefined)
    setFirstUnreadMessageIdValue(undefined)
  }, [])

  if (!conversation || !chatConfig) {
    return null
  }

  return (
    <SafeAreaView
      edges={['left', 'right', 'bottom']}
      style={[styles.container, getBackGroundColor()]}
    >
      {isNewsfeedLayout ? (
        <NewsfeedMessagesView
          conversation={conversation}
          onClearScrolling={onClearScrolling}
          onHideDescription={onHideDescription}
          onLongPress={onLongPress}
          getAllowedActions={getAllowedActions}
          onShowFile={onShowFile}
          onShowMultiAttachments={onShowMultiAttachments}
          onSendMessage={onSendMessage}
          onSendReaction={onSendReaction}
          onShowReactors={onShowReactors}
          parsePatterns={parsePatterns}
          onMessageAction={onMessageAction}
          user={user}
          readByCounts={readByCounts}
          getMessage={getMessage}
          getPinnedMessage={getPinnedMessage}
          onCreateComposeMessage={onCreateComposeMessage}
          chatMessages={chatMessages}
          getMessageGroup={getMessageGroup}
          getPinnedMessageGroup={getPinnedMessageGroup}
          pinnedChatMessages={pinnedChatMessages}
          messageIndex={messageIndex}
          onLinkPress={onLinkPress}
          isLoadingEarlier={isLoadingEarlier}
          hasMoreMessages={hasMoreMessages}
          onLoadEarlier={onLoadEarlier}
        />
      ) : (
        <BubbleMessagesView
          conversation={conversation}
          messageBody={messageBody}
          chatMessages={chatMessages}
          pinnedChatMessages={pinnedChatMessages}
          messageIndex={messageIndex}
          onLongPress={onLongPress}
          onShowFile={onShowFile}
          onSendMessage={onSendMessage}
          readByCounts={readByCounts}
          onSendReaction={onSendReaction}
          onShowMultiAttachments={onShowMultiAttachments}
          onShowReactors={onShowReactors}
          online={online}
          pasteAttachments={pasteAttachments}
          onMessageAction={onMessageAction}
          prefillMessageText={prefillMessageText}
          selectedAttachments={selectedAttachments}
          setChatMessageBody={setChatMessageBody}
          user={user}
          getMessage={getMessage}
          getPinnedMessage={getPinnedMessage}
          getMessageGroup={getMessageGroup}
          getPinnedMessageGroup={getPinnedMessageGroup}
          onLinkPress={onLinkPress}
          isUploading={isUploading}
          onPressAcknowledgeResource={onPressAcknowledgeResource}
          members={members}
          showPinned={showPinned}
          setShowPinned={setShowPinned}
          readOnly={readOnly}
          replyMessage={replyMessage}
          setReplyMessage={onSetReplyMessageAction}
          shouldFocusInput={shouldFocusInput}
          setShouldFocusInput={setShouldFocusInput}
          swipeableRef={swipeableRef}
          firstUnreadMessageId={firstUnreadMessageIdValue}
          unreadMessageCount={unreadMessageCount}
          firstUnreadMessageIndex={firstUnreadMessageIndexValue}
          onClearScrollToFirstUnreadMessage={onClearScrollToFirstUnreadMessage}
          onHideDescription={onHideDescription}
          onClearScrolling={onClearScrolling}
          getAllowedActions={getAllowedActions}
          isLoadingEarlier={isLoadingEarlier}
          hasMoreMessages={hasMoreMessages}
          onLoadEarlier={onLoadEarlier}
        />
      )}
      <ActionSheetMenu
        menuRef={menuRef}
        actions={currentActions}
        onAction={onAction}
        onClose={() => {
          if (!isEmojiKeyBoardOpen) {
            setCurrentMessage(undefined)
          }
        }}
        onPlusPress={setIsEmojiKeyBoardOpen}
      />
      <EmojiPicker
        onEmojiSelected={onEmojiSelected}
        open={isEmojiKeyBoardOpen}
        onClose={() => {
          setIsEmojiKeyBoardOpen(false)
          setCurrentMessage(undefined)
        }}
        theme={
          theme.mode === 'dark'
            ? {
                container: theme.colors.grey11,
                header: '#fff',
                search: {
                  text: '#fff',
                  placeholder: '#ffffff2c',
                  icon: '#fff',
                  background: theme.colors.grey10,
                },
                category: {
                  icon: '#766dfc',
                  iconActive: '#fff',
                  container: '#252427',
                  containerActive: '#766dfc',
                },
              }
            : undefined
        }
        enableSearchAnimation={true}
        enableRecentlyUsed={true}
        enableSearchBar={true}
        enableCategoryChangeGesture={false}
        expandable={false}
      />
    </SafeAreaView>
  )
}

const convertGroupToChatMessage = (
  user: User | undefined,
  group: Message[]
): ChatMessage => {
  const firstMessage = group[0]
  const hasAttachments = group.map(m => m.attachment).length > 0
  const isDeleted = firstMessage.isDeleted
  const body = isDeleted
    ? user?.id === firstMessage.deletedBy?.id
      ? t('youDeletedThisMessage')
      : t('messageDeletedBy', {
          name: `${firstMessage.deletedBy?.firstName} ${firstMessage.deletedBy?.lastName}`,
        })
    : firstMessage.body
  const isShareMessage =
    firstMessage.subType === 'shareFile' ||
    firstMessage.subType === 'shareFolder'
  const image =
    !isDeleted && (hasAttachments || isShareMessage) ? 'image' : undefined

  return {
    _id: firstMessage.id,
    text: body || '',
    image,
    createdAt: new Date(firstMessage.createdAt),
    user: {
      _id: firstMessage.user.id,
      name: `${firstMessage.user.firstName} ${firstMessage.user.lastName}${
        firstMessage.user.title
          ? ` (${firstMessage.user.title})`
          : firstMessage.user.refStoreId
          ? ` (${firstMessage.user.refStoreId})`
          : ''
      }`,
      avatar: firstMessage.user.photoUrl,
    },
  }
}

const useStyles = makeStyles(theme => ({
  container: {
    flex: 1,
  },
}))
